import { createContext, useContext, useState, useEffect, FC, PropsWithChildren, useMemo, useCallback } from 'react';
import type { ThemeModeTypes } from 'theme/types';

// Custom color themes
export interface CustomTheme {
  id: string;
  name: string;
  primary: string;
  secondary: string;
}

export const CUSTOM_THEMES: CustomTheme[] = [
  {
    id: 'indigo',
    name: 'ایندیگو',
    primary: '#4F46E5',
    secondary: '#7C3AED',
  },
  {
    id: 'emerald',
    name: 'زمردی',
    primary: '#059669',
    secondary: '#0D9488',
  },
  {
    id: 'rose',
    name: 'رُز',
    primary: '#E11D48',
    secondary: '#BE185D',
  },
  {
    id: 'amber',
    name: 'کهربایی',
    primary: '#D97706',
    secondary: '#EA580C',
  },
  {
    id: 'sky',
    name: 'آسمانی',
    primary: '#0284C7',
    secondary: '#4F46E5',
  },
];

interface ThemeContextType {
  mode: ThemeModeTypes;
  toggleMode: () => void;
  setMode: (mode: ThemeModeTypes) => void;
  customTheme: CustomTheme;
  setCustomTheme: (theme: CustomTheme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_MODE_KEY = 'app-theme-mode';
const CUSTOM_THEME_KEY = 'app-custom-theme';

export const ThemeContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeModeTypes>(() => {
    const saved = localStorage.getItem(THEME_MODE_KEY);
    return (saved as ThemeModeTypes) || 'light';
  });

  const [customTheme, setCustomThemeState] = useState<CustomTheme>(() => {
    const saved = localStorage.getItem(CUSTOM_THEME_KEY);
    return saved ? JSON.parse(saved) : CUSTOM_THEMES[0];
  });

  useEffect(() => {
    localStorage.setItem(THEME_MODE_KEY, mode || 'light');
  }, [mode]);

  useEffect(() => {
    localStorage.setItem(CUSTOM_THEME_KEY, JSON.stringify(customTheme));
  }, [customTheme]);

  const toggleMode = useCallback(() => {
    setModeState(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  const setMode = useCallback((newMode: ThemeModeTypes) => {
    setModeState(newMode);
  }, []);

  const setCustomTheme = useCallback((theme: CustomTheme) => {
    setCustomThemeState(theme);
  }, []);

  const value = useMemo(() => ({
    mode,
    toggleMode,
    setMode,
    customTheme,
    setCustomTheme,
  }), [mode, toggleMode, setMode, customTheme, setCustomTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider');
  }
  return context;
};
